import React, { useCallback, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';
import uniqueId from 'lodash/uniqueId';

import { useDeleteQuestionMutation } from 'api/main';
import { customTunnelSelectors } from 'reducers';
import {
    editingAnswerIdSet,
    editingQuestionIdSet,
    editingQuestionSet,
    newEditingQuestionIdSet,
    newStepQuestionRemoved,
} from 'reducers/customTunnel';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import QuestionForm from './QuestionForm';
import { ConfirmDialog } from 'components';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as icons from 'mdi-material-ui';

const iconSx = { cursor: 'pointer' };

const QuestionItem = ({ question, onRemove, pmQuestions, index, formCleanup }) => {
    const uId = useRef(uniqueId()); // unique id used for editing state and drag-n-drop
    const dispatch = useDispatch();

    const { ppId: editingTunnelStepId } = useSelector(customTunnelSelectors.getEditingStepId);
    const { ppId: editingQuestionId } = useSelector(customTunnelSelectors.getEditingQuestionId);
    const newEditingQuestionId = useSelector(customTunnelSelectors.getNewEditingQuestionId);
    const editingQuestion = useSelector(customTunnelSelectors.getEditingQuestion);

    const [deleteQuestion, { isLoading: deleteLoading, isSuccess: deleteSuccess }] =
        useDeleteQuestionMutation();

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();

    const handleEdit = useCallback(() => {
        // cancel any editing answer form
        dispatch(editingAnswerIdSet(null));
        // question form is kept in store
        dispatch(editingQuestionSet({ value: question }));
        // close the confirm edit dialog
        handleEditClose();
        // cleanup the invalid questions
        formCleanup();
        // set the current question as editing
        if (question?.id) {
            // editing an existing question
            dispatch(editingQuestionIdSet({ ppId: question?.id, pmId: pmQuestions?.[index]?.id }));
            dispatch(newEditingQuestionIdSet(null));
        } else {
            // editing a question that is not saved in DB
            dispatch(newEditingQuestionIdSet(uId.current));
            dispatch(editingQuestionIdSet(null));
        }
    }, [dispatch, formCleanup, handleEditClose, index, pmQuestions, question]);

    const handleConfirmEdit = useCallback(() => {
        if (editingQuestion) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingQuestion, handleEditOpen, handleEdit]);

    const handleDeleteQuestion = useCallback(
        index => () => {
            if (question?.id) {
                // question exists in the DB
                deleteQuestion({ ppId: question.id, pmId: pmQuestions[index].id });
            } else if (editingTunnelStepId) {
                // remove the question from a tunnel step that is being edited (exists in DB)
                onRemove();
            } else {
                // update the step being created in the store
                dispatch(newStepQuestionRemoved({ questionIndexe: index }));
            }
        },
        [question.id, deleteQuestion, pmQuestions, editingTunnelStepId, onRemove, dispatch]
    );

    useEffect(() => {
        if (deleteSuccess) {
            handleDeleteClose();
        }
    }, [handleDeleteClose, deleteSuccess]);

    useEffect(() => {
        if (!question?.title) {
            // set newly added question without a title as being edited
            dispatch(newEditingQuestionIdSet(uId.current));
        }
    }, [dispatch, question?.title]);

    return (
        <Draggable
            draggableId={`${question?.id || ''}-${pmQuestions?.[index]?.id || ''}-question-${
                uId.current
            }`}
            index={index}
            isDragDisabled={
                newEditingQuestionId === uId.current || editingQuestionId === question?.id
            }
        >
            {(provided, snapshot) => (
                <Paper
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    sx={theme => ({
                        padding: 1,
                        borderRadius: '3px',
                        marginY: 1,
                        border:
                            newEditingQuestionId === uId.current ||
                            editingQuestionId === question?.id
                                ? `3px solid ${alpha(theme.palette.primary.light, 0.5)}`
                                : '',

                        backgroundColor:
                            newEditingQuestionId !== uId.current &&
                            editingQuestionId !== question?.id
                                ? theme.palette.secondary.main
                                : 'initial',
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? '.75' : '1',
                    })}
                    elevation={3}
                >
                    {newEditingQuestionId !== uId.current && editingQuestionId !== question?.id && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography title={question.title} noWrap sx={{ maxWidth: '85%' }}>
                                {question.title}
                            </Typography>
                            <Box display="flex" alignItems="center">
                                <ConfirmDialog
                                    open={deleteOpen}
                                    handleConfirm={handleDeleteQuestion(index)}
                                    handleClose={handleDeleteClose}
                                    title={texts.DELETE_QUESTION}
                                    loading={deleteLoading}
                                    confirmText={texts.CONFIRMER}
                                    cancelText={texts.CANCEL}
                                >
                                    {texts.CONFIRM_DELETE_QUESTION}
                                </ConfirmDialog>
                                <ConfirmDialog
                                    open={confirmEditOpen}
                                    handleConfirm={handleEdit}
                                    handleClose={handleEditClose}
                                    title={texts.CHANGES_NOT_CONFIRMED}
                                    confirmText={texts.CONFIRMER}
                                    cancelText={texts.CANCEL}
                                >
                                    {texts.CONFIRM_DISCARD_CHANGES}
                                </ConfirmDialog>
                                <icons.Pencil
                                    sx={{ ...iconSx, '&:hover': { color: 'primary.main' } }}
                                    onClick={handleConfirmEdit}
                                />
                                <icons.Delete
                                    sx={{ ...iconSx, '&:hover': { color: 'error.main' } }}
                                    onClick={handleDeleteOpen}
                                />
                                <span {...provided.dragHandleProps} style={{ display: 'flex' }}>
                                    <icons.Drag
                                        sx={{
                                            ...iconSx,
                                            cursor: 'move',
                                            ':hover': { color: 'initial' },
                                        }}
                                    />
                                </span>
                            </Box>
                        </Box>
                    )}
                    <Collapse
                        in={
                            (newEditingQuestionId === uId.current ||
                                editingQuestionId === question?.id) &&
                            !!editingQuestion
                        }
                    >
                        {(newEditingQuestionId === uId.current ||
                            editingQuestionId === question?.id) &&
                            !!editingQuestion && ( // need to unmount children when closed
                                <QuestionForm
                                    newId={uId.current}
                                    question={question}
                                    onRemove={onRemove}
                                    pmQuestions={pmQuestions}
                                    questionIndex={index}
                                />
                            )}
                    </Collapse>
                </Paper>
            )}
        </Draggable>
    );
};

QuestionItem.propTypes = {
    question: PropTypes.object,
    onRemove: PropTypes.func,
    formCleanup: PropTypes.func,
    index: PropTypes.number,
    pmQuestions: PropTypes.array,
};

export default QuestionItem;
